import React, { useRef, useEffect, useMemo } from 'react';

import { useRecoilState, useResetRecoilState } from 'recoil';
import styled from 'styled-components';

import { addNodeMenuState } from '../atoms/add-node-menu-atom';
import { NodeType } from '../models';

export function AddNodeMenu() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [popupState] = useRecoilState(addNodeMenuState);
  const resetPopupState = useResetRecoilState(addNodeMenuState);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        resetPopupState();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [resetPopupState]);

  const handleOptionClick = (e: React.MouseEvent, nodeType: NodeType) => {
    e.preventDefault();
    e.stopPropagation();
    popupState.onAdd?.(nodeType);
    resetPopupState();
  };

  const position = useMemo(() => {
    if (!popupState.anchorEl) {
      return { x: 0, y: 0 };
    }

    const { left, top } = popupState.anchorEl.getBoundingClientRect();
    return { x: left, y: top };
  }, [popupState.anchorEl]);

  return (
    <HandleWrapper ref={wrapperRef} style={{ left: position.x, top: position.y }}>
      {popupState.isOpen && (
        <DropdownMenu>
          <MenuOption onClick={(e) => handleOptionClick(e, NodeType.ACTION)}>
            <MenuOptionTitle>Action</MenuOptionTitle>
            <MenuOptionDescription>Select the action you would like to message your audience</MenuOptionDescription>
          </MenuOption>
          <MenuOption onClick={(e) => handleOptionClick(e, NodeType.TIME_DELAY)}>
            <MenuOptionTitle>Time delay</MenuOptionTitle>
            <MenuOptionDescription>Set a delays and scheduled messaging</MenuOptionDescription>
          </MenuOption>
          {popupState.conditionalAllowed && (
            <MenuOption onClick={(e) => handleOptionClick(e, NodeType.CONDITIONAL)}>
              <MenuOptionTitle>Conditional split</MenuOptionTitle>
              <MenuOptionDescription>Decision point in workflow that segments based criteria</MenuOptionDescription>
            </MenuOption>
          )}
        </DropdownMenu>
      )}
    </HandleWrapper>
  );
}

const HandleWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translate(-50%, -50%);
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-grayscale-white);
  border-radius: var(--sizes-20);
  box-shadow: 0 var(--sizes-10) 12px rgba(0, 0, 0, 0.15);
  width: 243px;
  z-index: 1000;
`;

const MenuOption = styled.div`
  padding: var(--sizes-30) var(--sizes-70);
  cursor: pointer;
  border-bottom: 1px solid var(--color-gray-10);

  &:first-child {
    border-top-left-radius: var(--sizes-20);
    border-top-right-radius: var(--sizes-20);
  }

  &:last-child {
    border-bottom: none;
    border-bottom-left-radius: var(--sizes-20);
    border-bottom-right-radius: var(--sizes-20);
  }

  &:hover {
    background: var(--color-gray-10);
  }

  &:not(:first-child):not(:last-child):hover {
    border-radius: 0;
  }
`;

const MenuOptionTitle = styled.div`
  font: var(--font-regular-14pt-normal);
  margin-bottom: var(--sizes-10);
  color: var(--color-grayscale-black);
`;

const MenuOptionDescription = styled.div`
  color: var(--color-gray-70);
  font: var(--font-extra-small-12pt-normal);
`;
